'use client';

import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { cn } from '@/utils/cn';
import { useDispatch } from 'react-redux';
import { setSelectedOrder } from '@/redux/reducers/orders-reducer';
import { DEFAULT_COUNTRY_CODE } from '@/constants';
import { fetchGuestOrderDetails } from '@/redux/thunk/orders-thunk';
import PhoneNumberInput from '../Input/PhoneNumberInput';
import TextInput from '../Input/TextInput';
import Button from '../Button';
import CommonSnackBar from '../SnackBar/CommonSnackBar';
import TextLarge from '../Typography/TextLarge';
import TextSmall from '../Typography/TextSmall';
import ButtonLabel from '../Typography/ButtonLabel';

function CheckOrderStatusGuest({ onSubmitHandler }) {
  const router = useRouter();

  const [formData, setFormData] = useState({
    orderID: '',
    phone: '',
  });
  const [invalidRequest, setInvalidRequest] = useState(false);
  const dispatch = useDispatch();

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    const updateData = {
      ...formData,
      [name]: value,
    };
    setFormData(() => updateData);
  };

  // checking if order valid then go to route if not stay in the same page and show error msg
  const isOrderValid = async () => {
    try {
      const response = await dispatch(
        fetchGuestOrderDetails({
          orderID: formData.orderID,
          phone: `${DEFAULT_COUNTRY_CODE}${formData.phone}`,
        }),
      ).unwrap();
      if (response?.order_id) {
        const encodedPhone = btoa(formData.phone);
        const url = `/my-orders/details/${formData.orderID}?phone=${encodedPhone}`;
        setFormData({
          orderID: '',
          phone: '',
        });
        onSubmitHandler();
        router.push(url);
      } else {
        setInvalidRequest(true);
      }
    } catch (error) {
      setInvalidRequest(true);
    }
  };

  const handleRedirect = async () => {
    const regex = /^[a-zA-Z0-9]+$/;
    const testRegex = regex.test(formData?.orderID);
    if (
      formData.orderID.length === 10 &&
      testRegex &&
      formData.phone.length === 10
    ) {
      setInvalidRequest(false);
      dispatch(setSelectedOrder(null));
      await isOrderValid();
    } else {
      setInvalidRequest(true);
    }
  };

  const handleCloseToast = () => {
    setInvalidRequest(false);
  };

  useEffect(() => {
    setFormData({
      orderID: '',
      phone: '',
    });
  }, []);

  return (
    <>
      <div className="check-order-status-guest w-full">
        <TextLarge
          text="Check Order Status"
          className="text-coarse-wool text-lg lg:text-lg font-semibold mb-8"
        />

        <TextSmall
          text="Provide Order ID to check status"
          className="text-coarse-wool text-sm lg-text-sm font-normal mb-16 lg:mb-20"
        />

        <TextInput
          label="Order ID"
          className="mb-20"
          name="orderID"
          type="text"
          placeholder="Enter Order ID"
          value={formData?.orderID}
          onChangeHandler={(e) => handleInputChange(e)}
          maxLength={50}
          isRequired
        />

        <PhoneNumberInput
          value={formData?.phone}
          onChangeHandler={(e) => handleInputChange(e)}
          placeholder="1xxxxxxxxx"
          label="Phone number (billing/ shipping)"
          isRequired
          className="mb-20"
          name="phone"
        />

        <Button
          type="primary"
          className={cn(
            'w-full bg-tealgreen mx-auto mt-20 line-clamp-1 lg:h-[40px] lg:py-0',
            {
              'bg-aquarelle-purple border-[1px] !border-himalayan-poppy hover:bg-aquarelle-purple cursor-not-allowed':
                !formData.orderID.length || !formData.phone.length,
            },
          )}
          onClick={handleRedirect}
          disabled={!formData.orderID.length || !formData.phone.length}
        >
          <ButtonLabel
            label="Check Order Status"
            className={cn('text-white font-bold', {
              'text-atelier':
                !formData.orderID.length || !formData.phone.length,
            })}
          />
        </Button>
      </div>
      {invalidRequest ? (
        <CommonSnackBar
          title="Order not found. Please check your Order ID and phone number and try again."
          onView={invalidRequest}
          isSuccess={false}
          className="!bottom-[-90px] lg:!bottom-[10px]"
          onClose={() => handleCloseToast()}
        />
      ) : null}
    </>
  );
}

export default CheckOrderStatusGuest;
